import { useEffect, useState } from "react";
import Select from "react-select";
import LoadingIcon from "../../components/Loaders/LoadingIcon";
import axiosJobs, { JobWithRoutes } from "../../services/axios/jobs";
import SearchBar from "../../components/Filters/SearchBarWithFilters";
import { CurrentUser } from "../../store/storeTypes";
import { jobValidationStatus } from "../../pages/Admin/Jobs/EditJobPage";
import InfinitePageLoader from "../InfinitePageLoader";
import { formatAddress } from "../Cards/JobHistoryCard";
import { RoutesBubbles } from "../../pages/Admin/Routes/RoutePage/MapJobCard";
import { format } from "date-fns";
import { dateFromMongoId } from "../../utils/mongo";
import { useInfiniteQuery } from "@tanstack/react-query";
import useCityOptions from "../../hooks/useCityOptions";
import { Job } from "@mysnowscape/snow-lib";

const seasonOptions = [
  { value: "snow", label: "Snow" },
  { value: "landscape", label: "Landscape" },
];
const inactiveFilterOptions = [
  { value: false, label: "Active only" },
  { value: true, label: "Include inactive" },
];
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type PropertyFilters = Record<string, any>;
export default function PropertyList({
  currentUser,
  onJobClick,
  showFiltersDefault = true,
  filterContainerClass = "w-full",
  onJobSelected,
  selectedJobs = [],
  actionName = "view",
  showInactiveFilter = false,
  filters: controlledFilters,
  searchTerm: controlledSearchTerm,
  onFiltersChange,
  onSearchChange,
}: {
  currentUser: CurrentUser;
  onJobClick: (jobId: string) => void;
  showFiltersDefault?: boolean;
  filterContainerClass?: string;
  onJobSelected?: (job: JobWithRoutes, checked: boolean) => void;
  selectedJobs?: Job[];
  actionName?: string;
  showInactiveFilter?: boolean;
  // When `filters` is provided the list is controlled and renders whatever the
  // caller (the url-backed property page) passes in. Otherwise it owns its own
  // filter state, which is what the property picker and merge pages need.
  filters?: PropertyFilters;
  searchTerm?: string | null;
  onFiltersChange?: (filters: PropertyFilters) => void;
  onSearchChange?: (searchTerm: string | null) => void;
}) {
  const [ownFilter, setOwnFilter] = useState<PropertyFilters>({});
  const [ownSearchTerm, setOwnSearchTerm] = useState<string | null>(null);
  const isControlled = controlledFilters !== undefined;
  const currentFilter = isControlled ? controlledFilters : ownFilter;
  const searchTerm =
    controlledSearchTerm !== undefined ? controlledSearchTerm : ownSearchTerm;
  const [perPage] = useState(70);

  const query = useInfiniteQuery<JobWithRoutes[]>({
    queryKey: [
      "jobs",
      currentFilter,
      searchTerm,
      `company-${currentUser?.currentCompanyId}`,
    ],
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages, lastPageParam) =>
      lastPage.length < perPage
        ? null
        : (lastPageParam as unknown as number) + 1,
    queryFn: async ({ pageParam, signal }) => {
      const promise = axiosJobs.filterJobs(
        currentUser.currentCompanyId!,
        currentFilter,
        searchTerm,
        Number(pageParam) * perPage,
        perPage,
        signal,
      );
      return await promise;
    },
  });

  const { cityOptions } = useCityOptions(currentUser.currentCompanyId ?? null);

  useEffect(() => {
    // Uncontrolled lists keep their own filter state and clear it when the
    // account switches. A controlled list is reset by its owner instead.
    if (!isControlled) {
      setOwnFilter({});
    }
  }, [currentUser, isControlled]);

  const onSearch = (value: string | null) => {
    if (isControlled) {
      onSearchChange?.(value);
    } else {
      setOwnSearchTerm(value);
    }
  };

  const setFilters = (newFilters: PropertyFilters) => {
    if (isControlled) {
      onFiltersChange?.(newFilters);
    } else {
      setOwnFilter(newFilters);
    }
  };

  const makeSetFilterFieldFnc = (field: string) => {
    return (value: { value?: unknown } | null) => {
      setFilters({
        ...currentFilter,
        [field]: value?.value,
      });
    };
  };
  const setIncludeInactive = (include: boolean) => {
    const newFilters = { ...currentFilter };
    if (include) {
      newFilters.include_inactive = 1;
    } else {
      delete newFilters.include_inactive;
    }
    setFilters(newFilters);
  };
  return (
    <>
      <div className={`px-2 lg:px-10 w-full`}>
        <div className={filterContainerClass}>
          <SearchBar
            onSearch={onSearch}
            value={searchTerm ?? ""}
            showFiltersDefault={showFiltersDefault}
          >
            <Select
              onChange={makeSetFilterFieldFnc("season")}
              options={seasonOptions}
              placeholder="Filter by Season"
              isClearable={true}
              styles={{
                control: (baseStyles) => ({
                  ...baseStyles,
                  borderColor: "#829399",
                }),
              }}
              value={
                currentFilter?.season
                  ? seasonOptions.find((o) => o.value == currentFilter.season)
                  : null
              }
            />
            <Select
              onChange={makeSetFilterFieldFnc("city")}
              options={cityOptions}
              placeholder="Filter by City"
              isClearable={true}
              styles={{
                control: (baseStyles) => ({
                  ...baseStyles,
                  borderColor: "#829399",
                }),
              }}
              value={
                currentFilter?.city
                  ? cityOptions.find((o) => o.value == currentFilter.city)
                  : null
              }
            />
            {showInactiveFilter && (
              <Select
                onChange={(value) => setIncludeInactive(value?.value === true)}
                options={inactiveFilterOptions}
                placeholder="Filter by Status"
                isClearable={false}
                styles={{
                  control: (baseStyles) => ({
                    ...baseStyles,
                    borderColor: "#829399",
                  }),
                }}
                value={
                  currentFilter?.include_inactive
                    ? inactiveFilterOptions[1]
                    : inactiveFilterOptions[0]
                }
              />
            )}
          </SearchBar>
        </div>
        {query.isPending ? (
          <LoadingIcon />
        ) : (
          <>
            <InfinitePageLoader
              loadNextPage={query.fetchNextPage}
              hasMorePages={query.hasNextPage}
              loading={query.isFetchingNextPage}
              noMoreContent={
                showInactiveFilter && !currentFilter?.include_inactive ? (
                  <p>
                    Only active properties shown.{" "}
                    <button
                      type="button"
                      className="underline cursor-pointer"
                      onClick={() => setIncludeInactive(true)}
                    >
                      Include inactive
                    </button>
                  </p>
                ) : undefined
              }
            >
              <table className="table">
                <thead>
                  <tr>
                    {typeof onJobSelected === "function" && (
                      <th className="w-[16px]">
                        <input
                          type="checkbox"
                          onChange={(e) => {
                            for (const page of query.data?.pages ?? []) {
                              for (const p of page) {
                                onJobSelected(p, e.target.checked);
                              }
                            }
                            if (query.hasNextPage) {
                              alert(
                                "Note: there are more records matching your filters that were not selected.  If you want to select all records, scroll down until all records are shown and then press this checkbox again.",
                              );
                            }
                          }}
                        />
                      </th>
                    )}
                    <th>Property</th>
                    <th>Address</th>
                    <th className="hidden lg:table-cell">Routes</th>
                    <th className="hidden xl:table-cell">Created</th>
                    <th>{actionName}</th>
                  </tr>
                </thead>
                <tbody>
                  {query.data?.pages?.map((p) =>
                    p.map((j) => (
                      <tr
                        key={j._id.toString()}
                        onClick={() => onJobClick(j._id.toString())}
                        className={`cursor-pointer ${j.inactivatedAt != null ? "opacity-60" : ""}`}
                      >
                        {typeof onJobSelected === "function" && (
                          <td
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={
                                selectedJobs.find((s) => s._id === j._id) !=
                                null
                              }
                              onChange={(e) => {
                                onJobSelected(j, e.target.checked);
                              }}
                            />
                          </td>
                        )}
                        <td>
                          <div className="flex justify-between">
                            {j.name}{" "}
                            <div className="flex gap-1">
                              {jobValidationStatus(j).complete ? null : (
                                <div className="text-xs px-2 py-1 font-bold bg-orange text-white rounded-md inline">
                                  <span className="hidden lg:inline">
                                    Incomplete Data
                                  </span>
                                  <span className="lg:hidden">!</span>
                                </div>
                              )}
                              {(j.sharedWith?.length ?? 0) > 0 && (
                                <div className="text-xs px-2 py-1 font-bold bg-snow-green text-white rounded-md inline">
                                  <span className="hidden lg:inline">
                                    Shared
                                  </span>
                                </div>
                              )}
                              {j.inactivatedAt != null && (
                                <div className="text-xs px-2 py-1 font-bold bg-gray-300 text-gray-700 rounded-md inline">
                                  <span className="hidden lg:inline">
                                    Inactive
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td>{j.address && formatAddress(j)}</td>
                        <td className="hidden lg:table-cell">
                          <RoutesBubbles routes={j.routes} />
                        </td>
                        <td className="hidden xl:table-cell">
                          {format(dateFromMongoId(j._id), "MMM d yyyy")}
                        </td>
                        <td>
                          <a className="underline cursor-pointer">
                            {actionName}
                          </a>
                        </td>
                      </tr>
                    )),
                  )}
                </tbody>
              </table>
            </InfinitePageLoader>
          </>
        )}
      </div>
    </>
  );
}
